import React, { useEffect, useState } from "react";

// API From userApi
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useEditProjectMutation,
  useListOfProgramsQuery,
  useListOfProjectsQuery,
  useUserRolesQuery,
} from "../store/apis/userApi";

// Material UI Components
import AddIcon from "@mui/icons-material/Add";

// Custom mui Components
import Button from "../components/mui/Button";
import MagicDrawer from "../components/common/MagicDrawer_new";
import Table from "../components/common/MagicTable";
import Header from "../components/common/MagicPageHeader";
import ProjectForm from "../components/userManagement/ProjectForm";

// Types for TypeScript
import Text from "../components/mui/Text";
import ImageComponent from "../components/mui/Image";
import Dialog from "../components/common/Dialog";
import Modal from "../components/common/Modal";
import Stack from "../components/mui/Stack";
import Box from "../components/mui/Box";
import { Padding } from "@mui/icons-material";
import { showToast } from "../helper/toaster";

interface Props {
  selectedTab: any;
  chipLabelCount: number;
  projectAccess: any;
}

const ProjectTable: React.FC<Props> = ({ selectedTab, projectAccess }) => {
  const [params, setParams] = useState({
    pageNo: "1",
    pageSize: "10",
  });
  const [projectsDetails, setProjectsDetails] = useState<any>({
    projectName: "",
    moduleName: "",
    modules: [],
    subModuleName: [],
  });

  const [error, setError] = useState<any>({
    projectName: "",
    moduleName: "",
    modules: [],
    subModuleInput: [],
  });

  // Fetch project Data
  const { data: projectType = [], isLoading: isProjectLoading } =
    useListOfProjectsQuery();

  const [createProject, { isLoading: isLoadingAddCenter }] =
    useCreateProjectMutation();

  const [editProject, { isLoading: isEditProjectLoading }] =
    useEditProjectMutation();

  const [deleteProject, { isLoading: isDeleteProjectLoading }] =
    useDeleteProjectMutation();

  // Sidebar open state
  const [isOpenAddProgramsSidebar, setIsOpenAddProgramsSidebar] =
    useState<boolean>(false);
  // edit open state
  const [isOpenEditProgramsSidebar, setIsOpenEditProgramsSidebar] =
    useState<boolean>(false);
  // delete modal open close state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>({});

  // handle for add project sideBar
  const handleAddProjectsSideBar = () => {
    setIsOpenAddProgramsSidebar(!isOpenAddProgramsSidebar);
    setProjectsDetails({
      projectName: "",
      moduleName: "",
      modules: [],
      subModuleName: [],
    });
    setError({
      projectName: "",
      moduleName: "",
      modules: [],
      subModuleInput: [],
    });
  };

  const toggleEditProjectsSideBar = () => {
    setIsOpenEditProgramsSidebar(!isOpenEditProgramsSidebar);
    setError({
      projectName: "",
      moduleName: "",
      modules: [],
      subModuleInput: [],
    });
  };
  const toggleDeleteModal = () =>
    setIsDeleteModalOpen((prevState) => !prevState);

  const handleDelete = (data: any) => {
    // deleteProject({ scopeParentId: data?.value });
    toggleDeleteModal();
    setSelectedRowData(data);
  };

  const onDelete = async () => {
    try {
      const res = await deleteProject({
        scopeParentId: selectedRowData?.value,
      });

      if (res?.data?.success) {
        showToast(
          res?.data?.message || "Project deleted succesfully",
          "success"
        );
      }
    } catch (error) {
      console.log(error);
    }
    toggleDeleteModal();
  };

  // handle for add project sideBar
  const handleEditProjectsSideBar = (rowData: any) => {
    const scope =
      rowData?.scopes?.map((item: any) => ({
        scopeId: item?.value,
        name: item?.label,
        subModules: item?.children?.map((subItem: any) => subItem?.label) || [],
      })) || [];

    const preFieldData = {
      projectName: rowData.label,
      scopeName: rowData?.value,
      modules: scope,
      subModuleName: [],
    };
    setProjectsDetails(preFieldData);

    setIsOpenEditProgramsSidebar(!isOpenEditProgramsSidebar);
  };

  const handleOnChangeValue = (
    name: string,
    value: any,
    moduleIndex?: number
  ) => {
    let errors: any = {
      projectName: "",
      moduleName: "",
      modules: [],
      subModuleInput: [],
    };

    // Validate projectName
    if (
      !projectsDetails.projectName.trim() &&
      projectsDetails?.projectName === ""
    ) {
      errors.projectName = "Project name is required.";
    } else {
      errors.projectName = null;
    }

    // Validate modules
    if (projectsDetails.modules.length === 0) {
      errors.modulesName = "At least one module is required.";
    } else {
      errors.moduleName = "";
      // projectsDetails?.modules?.forEach((module: any, index: any) => {
      //   if (module?.subModules?.length === 0) {
      //     errors.modules[index] = `Must have at least one submodule.`;
      //   } else {
      //     delete errors.modules[index];
      //   }
      // });
    }

    // Update state based on input type
    setProjectsDetails((prev: any) => {
      if (name === "subModuleName" && moduleIndex !== undefined) {
        return {
          ...prev,
          subModuleName: {
            ...prev.subModuleName,
            [moduleIndex]: value,
          },
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });

    // Update errors
    setError(errors);
  };

  const onPageChange = async (event: any, page: number) => {
    setParams((prevParams) => ({
      ...prevParams,
      pageNo: page.toString(),
    }));
  };

  const validateProjectForm = () => {
    let errors: any = {
      projectName: "",
      moduleName: "",
      modules: [],
      subModuleInput: [],
    };

    // Validate projectName
    if (!projectsDetails.projectName.trim()) {
      errors.projectName = "Project name is required.";
    } else {
      errors.projectName = "";
    }

    // Validate modules
    if (projectsDetails?.modules?.length == 0) {
      errors.modulesName = "At least one module is required.";
    } else {
      errors.moduleName = "";
    }

    // Return false if all error values are empty, otherwise return true
    return Object.values(errors).every(
      (value) => value === "" || (Array.isArray(value) && value.length === 0)
    )
      ? false
      : true;
  };

  const handleOnAddProject = async () => {
    let newError = validateProjectForm();
    if (newError) {
      handleOnChangeValue("error", {});
      return;
    } else if (!newError) {
      try {
        const ProjectData = {
          scopeParentId: projectsDetails?.projectName
            .split(" ")
            .join("_")
            .toLowerCase(),
          scopeParentName: projectsDetails?.projectName.trim(),
          scopes: projectsDetails?.modules.map((module: any) => ({
            scopeId: module.name.split(" ").join("_").toLowerCase(),
            scopeName: module.name.trim(),
            scopeChild:
              module.subModules.map((childModule: any) => ({
                childId: childModule.split(" ").join("_").toLowerCase(),
                childName: childModule.trim(),
              })) || [],
          })),
        };
        const res = await createProject(ProjectData);
        if (res?.data?.success) {
          showToast(res?.data?.message || "Project created succesfully");
          setError("");
          handleAddProjectsSideBar();
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const onEditProjectDone = async () => {
    let newError = validateProjectForm();
    if (newError) {
      handleOnChangeValue("error", {});
      return;
    } else if (!newError) {
      try {
        const ProjectData = {
          scopeParentName: projectsDetails?.projectName.trim(),
          scopes: projectsDetails?.modules.map((module: any) => ({
            scopeId:
              module?.scopeId ||
              module?.name.split(" ").join("_").toLowerCase(),
            scopeName: module?.name?.trim(),
            scopeChild:
              module?.subModules?.map((childModule: any) => ({
                childId: childModule?.split(" ").join("_").toLowerCase(),
                childName: childModule?.trim(),
              })) || [],
          })),
        };
        let res = await editProject({
          payload: ProjectData,
          scopeParentName: projectsDetails?.scopeName,
        });

        if (res?.data?.success) {
          showToast(res?.data?.message || "Project edited succesfully");
        }
        setError("");
        toggleEditProjectsSideBar();
      } catch (error) {
        console.log(error);
      }
    }
  };

  // Add-Programs Btn
  const addProgramsBtn = [
    {
      text: "Add Projects",
      variant: "contained",
      startIcon: <AddIcon />,
      onClick: handleAddProjectsSideBar,
      sx: {
        borderRadius: "8px",
        border: "1px solid #a18218",
        backgroundColor: "#fff1b7",
        boxShadow: "none !important",
        "&:hover": {
          backgroundColor: "#e8d78e",
        },
      },
    },
  ];

  // Programs Table Columns
  const projectsColumns = [
    {
      field: "label",
      headerName: "Projects",
      flex: 1,
      minWidth: 220,
      sortable: false,
    },
    {
      field: "scopes",
      headerName: "Modules",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <>
            <Box
              sx={{
                display: "flex",
                gap: "5px",
                alignItems: "center",
                overflowX: "scroll",
                height: "100%",
                "&::-webkit-scrollbar": {
                  width: "0px",
                  height: "0px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
              }}
            >
              {params.row?.scopes?.map((moduleName: any, index: number) => (
                <Text
                  key={index}
                  sx={{
                    border: "1px solid #FEF7DA",
                    background: "#FEF7DA",
                    padding: "0px 10px",
                    borderRadius: "20px",
                    margin: "10px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  {moduleName.label}
                </Text>
              ))}
            </Box>
          </>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0,
      minWidth: 170,
      sortable: false,
      renderCell: (row: any) => {
        return (
          <>
            <Button
              variant="contained"
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                color: "black",
                boxShadow: "none",
                marginRight: "10px",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
              onClick={() => {
                handleEditProjectsSideBar(row?.row);
              }}
            >
              Edit
            </Button>
            <Button
              onClick={() => {
                handleDelete(row?.row);
              }}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                color: "black",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
                padding: "9px 10px",
              }}
            >
              {<ImageComponent src={"assets/images/login/Delete.svg"} />}
            </Button>
          </>
        );
      },
    },
  ];

  const totalPages = Math.ceil(projectType.length / 10);

  return (
    <>
      {/* Delete modal */}
      <Modal open={isDeleteModalOpen} disableClose={true}>
        <>
          <Box
            padding={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              flexDirection: "column",
            }}
          >
            <Box height={"50px"}>
              <ImageComponent src="/assets/images/icon/warning.svg" />
            </Box>
            <Text
              sx={{ color: "#101828", fontWeight: "600", fontSize: "18px" }}
            >
              Are you sure you want to delete this?
            </Text>
            <Text
              sx={{
                color: "#667085",
                fontSize: "13px",
                fontWeight: "400",
                textAlign: "center",
              }}
            >
              By clicking on the Delete button you are making sure that you want
              to delete this permanently.
            </Text>

            <Stack flexDirection={"row"} width={"100%"} gap="15px">
              <Button
                variant="contained"
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #8D8D8D",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#ededed",
                    boxShadow: "none",
                  },
                }}
                onClick={toggleDeleteModal}
              >
                No
              </Button>
              <Button
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #a18218",
                  backgroundColor: "#fef7da",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                    boxShadow: "none",
                  },
                }}
                loading={isDeleteProjectLoading}
                onClick={onDelete}
              >
                delete
              </Button>
            </Stack>
          </Box>
        </>
      </Modal>

      {/* Programs Table */}
      {selectedTab?.name === "Projects" && (
        <>
          <Header
            title="Projects"
            subTitle="Here you can view the list of all the Projects"
            chipLabel={`${projectType?.length || 0} Projects`}
            buttons={addProgramsBtn}
          />
          <Table
            count={totalPages}
            columns={projectsColumns}
            rows={projectType}
            loading={isProjectLoading}
            rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
            handlePageChange={onPageChange}
            headerSx={{
              textAlign: "start",
              fontWeight: 600,
              color: "#242424",
              padding: "13px 16px",
              backgroundColor: "#DADADA",
            }}
            currentPage={Number(params?.pageNo) || 1}
            tableSx={{ minHeight: "200px", width: "100%" }}
            sx={{
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#DADADA",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 550,
                fontSize: "14px",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
            getRowId={(row: any) => row?.id}
          />

          {/* This sidebar opens on clicking Add Programs button */}
          <MagicDrawer
            title="Add Projects"
            subtitle="Here you can Add Projects by filling the details"
            drawerOpen={isOpenAddProgramsSidebar}
            onDrawerToggle={handleAddProjectsSideBar}
            sx={{ width: "450px" }}
            actionComponent={() => (
              <>
                <Button
                  className="bun-outline"
                  onClick={handleAddProjectsSideBar}
                  disabled={isLoadingAddCenter}
                >
                  Cancel
                </Button>
                <Button
                  className="bun-filled"
                  onClick={handleOnAddProject}
                  loading={isLoadingAddCenter}
                >
                  Done
                </Button>
              </>
            )}
          >
            <ProjectForm
              projectsDetails={projectsDetails}
              error={error}
              validateProjectForm={validateProjectForm}
              handleOnChangeValue={handleOnChangeValue}
              setError={setError}
            />
          </MagicDrawer>

          {/* This sidebar opens on clicking Add Programs button */}
          <MagicDrawer
            title="Edit Projects"
            subtitle="Here you can Edit Projects"
            drawerOpen={isOpenEditProgramsSidebar}
            onDrawerToggle={toggleEditProjectsSideBar}
            sx={{ width: "450px" }}
            actionComponent={() => (
              <>
                <Button
                  className="bun-outline"
                  onClick={toggleEditProjectsSideBar}
                  disabled={isEditProjectLoading}
                >
                  Cancel
                </Button>
                <Button
                  className="bun-filled"
                  onClick={onEditProjectDone}
                  loading={isEditProjectLoading}
                >
                  Done
                </Button>
              </>
            )}
          >
            <ProjectForm
              projectsDetails={projectsDetails}
              error={error}
              validateProjectForm={validateProjectForm}
              handleOnChangeValue={handleOnChangeValue}
              setError={setError}
            />
          </MagicDrawer>
        </>
      )}
    </>
  );
};

export default ProjectTable;
